import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { secureApiService } from "../api/apiService";
import { useInactivityTimeout } from "../hooks/useInactivityTimeout";

// ─────────────────────────────────────────────────────────────────────────────
// RÔLES — source de vérité unique pour tout le frontend
// ─────────────────────────────────────────────────────────────────────────────
export const ROLES = Object.freeze({
  CLIENT:        'CLIENT',
  DRIVER:        'DRIVER',
  STAFF:         'STAFF',          // chef d'agence + agents (panel admin filtré par rôle)
  ADMIN_COMPANY: 'ADMIN_COMPANY',
  SUPER_ADMIN:   'SUPER_ADMIN',
});

// Rôles RBAC "staff" → mappés sur ROLES.STAFF (utilisent le panel admin filtré).
const STAFF_RBAC_ROLES = ['CHEF_AGENCE', 'RESERVATION_AGENT', 'TRAFFIC_AGENT', 'FINANCE_AGENT', 'SUPPORT_AGENT', 'OPERATOR'];

// Rôles côté "panel admin" (stockage adminToken, redirection /admin)
export const isAdminSideRole = (r) =>
  r === ROLES.SUPER_ADMIN || r === ROLES.ADMIN_COMPANY || r === ROLES.STAFF;

// Route d'accueil par rôle (après login ou guard redirect)
// 🏠 CLIENT → '/' : la page d'accueil publique (= la vue Trajets, cf. Lot B). AVANT c'était
// '/user/home' qui rend le composant Profile → un client connecté « à froid » atterrissait
// sur son PROFIL au lieu de l'accueil (bug signalé). On ne renvoie JAMAIS le client vers son
// profil par défaut : soit il revient à sa page d'origine (redirectAfterLogin), soit à l'accueil.
export const ROLE_HOME = {
  [ROLES.CLIENT]:        '/',
  [ROLES.DRIVER]:        '/driver/dashboard',
  [ROLES.STAFF]:         '/admin/dashboard',
  [ROLES.ADMIN_COMPANY]: '/admin/dashboard',
  [ROLES.SUPER_ADMIN]:   '/admin/dashboard',
};

// ─────────────────────────────────────────────────────────────────────────────
// DÉTECTION DU RÔLE — priorité : RBAC table > flags JWT > type_utilisateur legacy
// ─────────────────────────────────────────────────────────────────────────────
// allowAdmin=false → contexte PORTAIL PUBLIC/DRIVER : on ne résout JAMAIS un rôle admin
// (un compte multi-rôles connecté sur le site public/chauffeur reste DRIVER/CLIENT ; les
// rôles admin ne s'activent QUE via le portail admin). Évite l'auto-sélection du « plus
// haut rôle » qui envoyait un chauffeur dans l'espace admin.
// allowDriver=false (site public www) : un compte multi-rôle (chauffeur+client) n'est JAMAIS
// résolu DRIVER → traité en CLIENT et reste sur le portail public. Le rôle DRIVER n'est résolu
// que sur driver.gowele.com (et en dev/preview). Cohérent avec le scope backend application='public'.
export const detectRole = (userData, { allowAdmin = true, allowDriver = true } = {}) => {
  if (!userData) return null;

  // P1 : tableau roles du backend (user_roles table)
  const rbacRoles = userData.roles || [];
  if (allowAdmin) {
    if (rbacRoles.includes('SUPER_ADMIN'))   return ROLES.SUPER_ADMIN;
    if (rbacRoles.includes('ADMIN_COMPANY')) return ROLES.ADMIN_COMPANY;
    if (rbacRoles.some(r => STAFF_RBAC_ROLES.includes(r))) return ROLES.STAFF;
  }
  if (allowDriver && rbacRoles.includes('DRIVER')) return ROLES.DRIVER;
  if (rbacRoles.includes('CLIENT'))        return ROLES.CLIENT;

  // P2 : flag is_super_admin (payload JWT admin)
  if (allowAdmin && userData.is_super_admin === true) return ROLES.SUPER_ADMIN;

  // P3 : type_utilisateur legacy (colonne DB)
  switch (userData.type_utilisateur) {
    case 'super_admin': return allowAdmin ? ROLES.SUPER_ADMIN : ROLES.CLIENT;
    case 'admin':       return allowAdmin ? ROLES.ADMIN_COMPANY : ROLES.CLIENT;
    case 'conducteur':  return allowDriver ? ROLES.DRIVER : ROLES.CLIENT;
    case 'chauffeur':   return allowDriver ? ROLES.DRIVER : ROLES.CLIENT;
    case 'client':      return ROLES.CLIENT;
    default: break;
  }

  // P4 : champ role (payload JWT client)
  if (userData.role === 'conducteur') return allowDriver ? ROLES.DRIVER : ROLES.CLIENT;
  if (userData.role === 'client')     return ROLES.CLIENT;

  return ROLES.CLIENT;
};

// Le rôle DRIVER n'est résolu que sur le PORTAIL CHAUFFEUR (driver.gowele.com) ou en dev/preview.
// Sur le site public prod (www/gowele.com), un compte multi-rôle reste CLIENT (jamais happé vers
// /driver/dashboard). Point A : seule une connexion explicite sur driver.gowele.com ouvre le portail driver.
export const isDriverPortalHost = () => {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  if (h === 'driver.gowele.com') return true;
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.vercel.app')) return true; // dev/preview
  return false; // www.gowele.com / gowele.com / autres origines publiques prod
};

// Vrai SEULEMENT si le compte n'a QUE des rôles admin (aucun rôle client/chauffeur).
// → un tel compte doit passer par le portail admin ; un compte multi-rôles avec un rôle
//   client/chauffeur se connecte normalement sur le site public/chauffeur (pas de blocage).
export const isPureAdminAccount = (userData) => {
  if (!userData) return false;
  const roles = userData.roles || [];
  const hasDriver = roles.includes('DRIVER') || ['chauffeur', 'conducteur'].includes(userData.type_utilisateur);
  const hasClient = roles.includes('CLIENT') || userData.type_utilisateur === 'client';
  const admin = detectRole(userData, { allowAdmin: true });
  const isAdminLike = admin === ROLES.SUPER_ADMIN || admin === ROLES.ADMIN_COMPANY || admin === ROLES.STAFF;
  return isAdminLike && !hasDriver && !hasClient;
};

// ─────────────────────────────────────────────────────────────────────────────
// NETTOYAGE COMPLET — appelé avant chaque nouvelle session
// ─────────────────────────────────────────────────────────────────────────────
const cleanupAllAuthState = () => {
  // Purge TOTALE : identité, tokens, ET l'horodatage d'inactivité (sinon un reste fausserait
  // le prochain calcul d'expiration). Couvre aussi l'invité (authToken/user éphémères).
  ['user', 'authToken', 'adminToken', 'admin', 'wele_last_activity'].forEach(k => localStorage.removeItem(k));
  ['authToken', 'redirectAfterLogin', 'adminRedirectAfterLogin'].forEach(k => sessionStorage.removeItem(k));
};

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un AuthProvider');
  return ctx;
};

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const { t } = useTranslation('common');
  const [user,              setUser]              = useState(null);
  const [role,              setRole]              = useState(null);
  const [isAuthenticated,   setIsAuthenticated]   = useState(false);
  const [loading,           setLoading]           = useState(true);
  const [inactivityWarning, setInactivityWarning] = useState(false);
  const checkingRef = useRef(false);

  const _setAuth = useCallback((userData, detectedRole) => {
    setUser(userData);
    setRole(detectedRole);
    setIsAuthenticated(true);
    setLoading(false);
  }, []);

  const _clearAuth = useCallback(() => {
    setUser(null);
    setRole(null);
    setIsAuthenticated(false);
    setLoading(false);
    // 🧹 Point 3 (audit sessions) : purger l'identité en CACHE localStorage dès qu'on n'est plus
    // authentifié (ex. la revalidation silencieuse au retour d'onglet a échoué : session expirée).
    // Sans ça, le nom/prénom mis en cache restait affiché par des composants qui lisent
    // directement localStorage (ex. CartContext) PENDANT que l'UI redemande une connexion →
    // fenêtre incohérente « nom affiché + demande de reconnexion ». On n'efface QUE l'identité
    // client (user/authToken) — pas l'adminToken, indépendant et géré à part.
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
    } catch (_) { /* private mode / storage indisponible : sans effet */ }
  }, []);

  // ── LOGOUT ──────────────────────────────────────────────────────────────────
  // reason : USER_ACTION | TOKEN_EXPIRED | SECURITY_POLICY | ADMIN_REVOKE | SESSION_REVOKED …
  // On journalise la RAISON et l'ORIGINE (stack) pour diagnostiquer les déconnexions
  // automatiques (cf. audit sessions). Aucune redirection cross-application.
  const logout = useCallback(async (reason = 'USER_ACTION') => {
    try {
      const origin = (new Error().stack || '').split('\n')[2]?.trim() || '';
      // eslint-disable-next-line no-console
      console.warn(`[LOGOUT_REASON] ${reason}${origin ? ` · origine: ${origin}` : ''}`);
    } catch (_) { /* ignore */ }
    try { await secureApiService.post('/auth/logout', { reason }); } catch (_) {}
    cleanupAllAuthState();
    _clearAuth();
    // 💤 Déconnexion par INACTIVITÉ : on mémorise la raison (sessionStorage, survit à la
    // redirection) pour afficher un message clair sur la page de connexion.
    if (reason === 'INACTIVITY') {
      try { sessionStorage.setItem('wele_session_expired', 'inactivity'); } catch (_) { /* private mode */ }
    }
    if (!window.location.pathname.includes('/login')) {
      window.location.replace('/login');
    }
  }, [_clearAuth]);

  // ── VÉRIFICATION AUTH AU MONTAGE ─────────────────────────────────────────
  const checkAuthStatus = useCallback(async (opts) => {
    if (checkingRef.current) return;
    // Mode « silent » (re-validation au retour d'onglet) : on NE montre PAS le loader plein écran
    // → la vérification est transparente, comme sur les meilleurs sites.
    const silent = opts === true || opts?.silent === true;
    checkingRef.current = true;
    if (!silent) setLoading(true);

    // 🔒 Point 3 : si /auth/verify répond 401 (session client DÉFINITIVEMENT invalide/expirée),
    // on ne doit PAS restaurer depuis le cache localStorage non vérifié (P3) — sinon le nom
    // s'affiche pour une session morte. On distingue un 401 (rejet ferme) d'une erreur réseau
    // transitoire (où le cache reste un repli légitime).
    let clientAuthRejected = false;
    try {
      // ── P1 : cookie httpOnly → CLIENT ou DRIVER ──
      try {
        const res = await secureApiService.get('/auth/verify');
        if (res.data?.success && res.data?.user) {
          const u = res.data.user;
          // Cookie client = portail public/chauffeur → jamais de rôle admin (allowAdmin:false).
          // allowDriver piloté par le host : DRIVER seulement sur driver.gowele.com (point A).
          const r = detectRole(u, { allowAdmin: false, allowDriver: isDriverPortalHost() });
          // Seuls CLIENT et DRIVER peuvent s'authentifier via cookie client
          if (r === ROLES.CLIENT || r === ROLES.DRIVER) {
            // 🔑 PORTAIL : sur le site public / chauffeur (driver.gowele.com), la session
            // cookie CLIENT/CHAUFFEUR PRIME — un adminToken résiduel ne doit PAS l'empêcher
            // (sinon, après login chauffeur, le rechargement retombait sur P2 (admin) qui
            //  échoue → déconnexion → « rien ne se passe »). On ne défère à P2 (admin) QUE
            // sur les sous-domaines admin/company.
            const host = (typeof window !== 'undefined' && window.location.hostname) || '';
            const onAdminHost = /^(admin|company)\./i.test(host);
            const adminToken = localStorage.getItem('adminToken');
            const adminTokenPresent = adminToken && adminToken !== 'undefined' && adminToken !== 'null';
            if (!onAdminHost || !adminTokenPresent) {
              _setAuth(u, r);
              localStorage.setItem('user', JSON.stringify(u));
              return;
            }
          }
        }
      } catch (p1Err) {
        // 401 = cookie client invalide/expiré (rejet ferme). Autres (réseau/cold start) = transitoire.
        if (p1Err?.response?.status === 401) clientAuthRejected = true;
      }

      // ── P2 : token admin localStorage → ADMIN_COMPANY ou SUPER_ADMIN ──
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken && adminToken !== 'undefined' && adminToken !== 'null') {
        // 🔒 Même règle que P1 (ligne 196) : on ne résout un rôle ADMIN que sur un host
        // admin/company. Sur le site public/chauffeur, un compte MULTI-RÔLE (client + staff)
        // NE DOIT JAMAIS basculer côté admin à la restauration de session (sinon _setAuth(admin)
        // → ROLE_HOME=/admin + useUser()=null → l'espace client lui est refusé à tort).
        const host = (typeof window !== 'undefined' && window.location.hostname) || '';
        const onAdminHost = /^(admin|company)\./i.test(host);
        try {
          const res = await secureApiService.get('/admin/auth/verify', {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          if (res.data?.success && res.data?.user) {
            const u = res.data.user;
            const r = detectRole(u, { allowAdmin: onAdminHost, allowDriver: isDriverPortalHost() });
            if (isAdminSideRole(r)) {
              _setAuth(u, r);
              localStorage.setItem('admin', JSON.stringify(u));
              return;
            }
          }
          // Réponse reçue mais token invalide (401/403) → purger
          localStorage.removeItem('adminToken');
          localStorage.removeItem('admin');
        } catch (verifyErr) {
          // 🔒 NE PURGER QUE SUR 401 (token invalide/expiré). Un 403 = authentifié mais
          // l'endpoint a refusé l'autorisation → NE DOIT PAS détruire la session, sinon
          // un simple 403 (ex. déploiement backend en retard) déconnecte les agents et
          // toutes les requêtes /admin partent ensuite sans Bearer → 404 « Route non trouvée ».
          const isAuthError = verifyErr?.response?.status === 401;
          if (isAuthError) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('admin');
          } else {
            // 403 / cold start / erreur réseau → on garde le token et on restaure depuis le cache
            const savedAdmin = localStorage.getItem('admin');
            if (savedAdmin) {
              try {
                const u = JSON.parse(savedAdmin);
                // Même garde host-aware qu'au-dessus : pas d'élévation admin hors host admin/company.
                const r = detectRole(u, { allowAdmin: onAdminHost, allowDriver: isDriverPortalHost() });
                if (isAdminSideRole(r)) {
                  _setAuth(u, r);
                  return;
                }
              } catch (_) {}
            }
          }
        }
      }

      // ── P3 : fallback localStorage legacy (token non-admin) ──
      // 🔒 Point 3 : on NE restaure PAS depuis le cache si /auth/verify a renvoyé un 401 (session
      // client bien invalide) → évite d'afficher un nom pour une session morte. Le cache reste un
      // repli UNIQUEMENT sur erreur réseau/transitoire (clientAuthRejected=false).
      const legacyToken = localStorage.getItem('authToken');
      const savedUser   = localStorage.getItem('user');
      if (!clientAuthRejected && legacyToken && legacyToken !== 'undefined' && savedUser) {
        try {
          const parsed = JSON.parse(savedUser);
          if (parsed?.id && parsed?.email) {
            const r = detectRole(parsed, { allowAdmin: false, allowDriver: isDriverPortalHost() });
            if (r === ROLES.CLIENT || r === ROLES.DRIVER) {
              _setAuth(parsed, r);
              return;
            }
          }
        } catch (_) {}
      }

      _clearAuth();
    } catch (_) {
      _clearAuth();
    } finally {
      if (!silent) setLoading(false);
      checkingRef.current = false;
    }
  }, [_setAuth, _clearAuth]);

  // ⚠️ UNE SEULE FOIS au montage. Avec `[checkAuthStatus]`, toute recréation de la fonction
  // (ex. re-render de l'app déclenché par le poll de statut API toutes les 2 min) relançait la
  // vérification → setLoading(true) → loader « vérification… » qui démonte l'arbre → remontage
  // → la page de succès rejouait confirmPayment EN BOUCLE (~2 min). On la lance donc au montage seul.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { checkAuthStatus(); }, []);

  // 🌐 Comportement « meilleurs sites » : l'onglet peut rester ouvert des jours/mois SANS action
  // → aucun polling, aucune boucle. On re-valide UNIQUEMENT au RETOUR sur l'onglet (visibilité/
  // focus) et seulement après une absence significative (>60 s) → si le jeton a expiré pendant
  // l'inactivité, l'état se met à jour proprement (déconnexion douce) au lieu d'un 401 surprise.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let lastCheck = Date.now();
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheck < 60_000) return; // débounce → jamais de boucle
      lastCheck = Date.now();
      checkAuthStatus({ silent: true }); // re-validation transparente (pas de flash loader)
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);

  // ── LOGIN ────────────────────────────────────────────────────────────────────
  const login = useCallback((userData, token = null, redirectPath = null, opts = {}) => {
    if (!userData) return false;
    // Le PORTAIL décide : seul le portail admin (opts.adminPortal) active un rôle admin.
    // Une connexion publique/chauffeur reste DRIVER/CLIENT, même si le compte a aussi un
    // rôle admin (plus de bascule automatique vers l'espace admin).
    // allowDriver piloté par le host : sur www un multi-rôle reste CLIENT (point A) ; DRIVER
    // uniquement sur driver.gowele.com. adminPortal active le rôle admin (portail admin only).
    const detectedRole = detectRole(userData, { allowAdmin: opts.adminPortal === true, allowDriver: isDriverPortalHost() });
    if (!detectedRole) return false;

    // Read admin redirect BEFORE cleanup clears it
    const adminRedirect = sessionStorage.getItem('adminRedirectAfterLogin');

    cleanupAllAuthState();

    // Stockage par type de rôle
    if (isAdminSideRole(detectedRole)) {
      // ⚠️ On NE révoque PLUS la session client ici (cause de logout automatique + conflit
      // inter-portails). L'isolation est assurée côté backend : sur les routes /admin/*,
      // le token Bearer admin prime sur le cookie client (cf. middleware/auth.js).
      if (token) localStorage.setItem('adminToken', token);
      localStorage.setItem('admin', JSON.stringify(userData));
    } else {
      if (token) localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
    }

    _setAuth(userData, detectedRole);

    // Redirect sécurisé — anti open-redirect + isolation par rôle
    const stored = sessionStorage.getItem('redirectAfterLogin') || adminRedirect;
    sessionStorage.removeItem('redirectAfterLogin');
    const requested = redirectPath || stored || ROLE_HOME[detectedRole];

    const safeRedirect = (() => {
      if (!requested || !requested.startsWith('/') || requested.startsWith('//') || requested.includes(':')) {
        return ROLE_HOME[detectedRole];
      }
      const allowedPrefix = {
        [ROLES.CLIENT]:        '/user',
        [ROLES.DRIVER]:        '/driver',
        [ROLES.STAFF]:         '/admin',
        [ROLES.ADMIN_COMPANY]: '/admin',
        [ROLES.SUPER_ADMIN]:   '/admin',
      }[detectedRole];
      // Pages publiques accessibles après login, QUEL QUE SOIT le rôle → on revient EXACTEMENT
      // à l'étape du funnel de réservation (et non vers le portail du rôle). Couvre le cas
      // « session expirée pendant le paiement » : un chauffeur/agent qui réserve comme passager
      // doit revenir sur /paiement, pas être renvoyé vers /driver ou /admin.
      // (ProtectedPublic autorise déjà client + chauffeur sur ces routes ; un 'admin' y sera
      //  renvoyé vers /admin par ProtectedPublic, ce qui reste cohérent.)
      // La racine exacte '/' (accueil public = vue Trajets) est toujours une cible valide.
      if (requested === '/') return '/';
      const publicAllowed = ['/avis', '/verify', '/travels', '/trajets',
        '/paiement', '/checkout', '/panier', '/mes-reservations', '/reservation', '/confirmation'];
      if (publicAllowed.some(p => requested.startsWith(p))) return requested;
      return requested.startsWith(allowedPrefix) ? requested : ROLE_HOME[detectedRole];
    })();

    setTimeout(() => window.location.replace(safeRedirect), 200);
    return true;
  }, [_setAuth]);

  // ── MISE À JOUR PROFIL ───────────────────────────────────────────────────
  const updateUser = useCallback((newData) => {
    if (!newData || typeof newData !== 'object') return false;
    setUser(prev => {
      const merged = { ...prev, ...newData };
      try {
        const key = isAdminSideRole(role) ? 'admin' : 'user';
        localStorage.setItem(key, JSON.stringify(merged));
      } catch (_) {}
      return merged;
    });
    return true;
  }, [role]);

  // ── INACTIVITÉ ───────────────────────────────────────────────────────────
  useInactivityTimeout({
    enabled: isAuthenticated,
    timeout: 20 * 60 * 1000,   // 20 min d'inactivité (validé) — distinct de l'expiration du JWT.
    warningBefore: 60 * 1000,
    onWarning: () => setInactivityWarning(true),
    onLogout:  () => { setInactivityWarning(false); logout('INACTIVITY'); },
  });

  // ── RBAC HELPERS ─────────────────────────────────────────────────────────
  const roles      = user?.roles || [];
  const company_id = user?.company_id ?? null;
  const agence_id  = user?.agence_id ?? null;

  const value = {
    // State
    user,
    role,
    isAuthenticated,
    // 🔒 RC-3 : session de CHECKOUT invité (token guest:true).
    isGuest: !!user?.guest,
    // 🛒 POINT 4 — CONDITION UNIQUE « peut utiliser le panier » = VRAI COMPTE connecté (jamais un
    // invité). SOURCE DE VÉRITÉ CENTRALISÉE : Header, BottomNav ET ReservationForm doivent TOUS
    // utiliser `canUseCart` (et pas `isAuthenticated` chacun de leur côté), sinon le panier « fuit »
    // vers l'invité dans un composant oublié. C'était la cause de la persistance du bug.
    canUseCart: isAuthenticated && !user?.guest,
    loading,
    // Actions
    login,
    logout,
    updateUser,
    checkAuthStatus,
    // Booléens de rôle
    isClient:       () => role === ROLES.CLIENT,
    isDriver:       () => role === ROLES.DRIVER,
    isAdminCompany: () => role === ROLES.ADMIN_COMPANY,
    isSuperAdmin:   () => role === ROLES.SUPER_ADMIN,
    isStaff:        () => role === ROLES.STAFF,
    isAnyAdmin:     () => role === ROLES.SUPER_ADMIN || role === ROLES.ADMIN_COMPANY,
    isPanelUser:    () => isAdminSideRole(role),   // admin OU staff → accès panel
    // RBAC granulaire (depuis user_roles table)
    roles,
    company_id,
    agence_id,
    hasRole:      (...requiredRoles) => requiredRoles.some(r => roles.includes(r)) || role === ROLES.SUPER_ADMIN,
    hasExactRole: (...requiredRoles) => requiredRoles.every(r => roles.includes(r)) || role === ROLES.SUPER_ADMIN,
    // Alias legacy
    userId:    user?.id,
    userEmail: user?.email,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {inactivityWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '32px 28px', maxWidth: 360,
            width: '90%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏱️</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('inactivity.expiringSoon')}</h3>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
              {t('inactivity.willLogout')}
            </p>
            <button
              onClick={() => setInactivityWarning(false)}
              style={{
                background: '#667eea', color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 28px', fontSize: 15, cursor: 'pointer', width: '100%',
              }}>
              {t('inactivity.stayConnected')}
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
